<script setup>
// Astra DS · SiteHeader — la cabecera consola.
//
// Figma: Pattern/SiteHeader-Reset 652:426.
//
// «Cabecera consola. Flex, no grid: las celdas aparecen y desaparecen según el
// ancho, y las columnas fijas dejarían hueco muerto a la derecha. Bordes por
// celda, no gutters.»
//
// De izquierda a derecha: marca · navegación · aire · player · idioma + cuenta.
//
// 🔴 **62 de alto en escritorio desde el 04.11.26** (`Pattern/SiteHeader
// 652:426`, DESIGN.md § La cabecera de 70 y el selector de idioma): las celdas
// siguen a 46 y la barra lleva **8** de aire arriba y abajo. El nodo dice 12;
// Pablo lo bajó a 8 al verlo montado. Sin filetes entre
// celdas, sin la celda de tiendas, el player en una caja de radio 4 y el selector
// de idioma junto a la cuenta. **En móvil no cambia nada**: 46, como estaba.
//
// 🔴 **Aquí había una quinta celda**, el readout —`TRANSMISIÓN ACTIVA · SEÑAL
// ESTABLE · hora`—, 205 px de lectura de consola en una barra que ya dice lo
// importante en voz alta: el player nombra lo que suena. Se retiró el 07.09.26
// con su filete y sus dos media queries. Los 205 px se los queda la navegación,
// que ya era flexible. **No se sustituye por una versión corta**: si el dato
// vuelve, vuelve a una pantalla de estado, no al marco de la página.
//
// ⚠️ **Y con él se fueron las props `readout` y `readoutShort`**, que sobrevivían
// solo porque las usaba la celda de móvil. Desde el 11.09.26 esa celda dice el
// programa y su género, así que ya no las necesita nadie: se retiran, y con
// ellas el reloj que las componía en `App.vue`.
//
// El player vive aquí, en toda la web. Es la razón de que la cabecera sea una
// consola y no una barra de enlaces.
import { useI18n } from "vue-i18n";
import AstraMark from "./AstraMark.vue";
import AstraWord from "./AstraWord.vue";
import NavItem from "./NavItem.vue";
import BanderaIdioma from "./BanderaIdioma.vue";
import MenuIdioma from "./MenuIdioma.vue";
import TooltipCabecera from "./TooltipCabecera.vue";
import AstraIcon from "@/components/icons/AstraIcon.vue";
import ButtonDs from "@/components/ds/ButtonDs.vue";
import MiniWave from "./MiniWave.vue";
import SignalDot from "./SignalDot.vue";
import NavSheet from "./NavSheet.vue";
import AccountMenu from "./AccountMenu.vue";
import VolumenEmision from "./VolumenEmision.vue";
import PlayerDesplegable from "./PlayerDesplegable.vue";
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { IDIOMA, NOMBRE_DEL_IDIOMA, cambiaIdioma } from "@/i18n/idioma";

const props = defineProps({
  // [{ label, to }] — la sección activa se decide comparando con `current`.
  items: { type: Array, default: () => [] },
  current: { type: String, default: "" },

  // Player
  playing: { type: Boolean, default: false },

  // 🔴 **Lo que suena, en la cabecera** (21.10.26, entrega
  // `2026-10-21-portada-franja-y-player-en-cabecera.md`): { portada, artista,
  // cancion, album, programa, franja, transcurrido, duracion }. Lo prepara
  // `App.vue`. Y el volumen de la emisión, de 0 a 1 (`v-model:volumen`).
  sonando: { type: Object, default: () => ({}) },
  volumen: { type: Number, default: 1 },

  live: { type: Boolean, default: true },
  // 🔴 **El nombre y su franja llegan por separado**, no en una cadena. En la
  // barra apretada el nombre se acorta con puntos suspensivos y **las horas no
  // se acortan nunca**: el nombre dice qué suena y se reconoce cortado, las
  // horas dicen cuánto queda y cortadas no dicen nada. Diseño, 12.09.26.
  programme: { type: String, default: "" },
  programmeRange: { type: String, default: "" },

  // Para la celda de móvil, que dice el programa y su género. Van aparte
  // porque ahí el horario no cabe.
  programmeName: { type: String, default: "" },
  programmeGenre: { type: String, default: "" },

  // Las amplitudes del audio y si este navegador las entrega. Con señal manda
  // la onda, que dice cómo suena; sin ella el punto, que dice que suena.
  levels: { type: Array, default: () => [] },
  analisis: { type: String, default: "esperando" },

  theme: { type: String, default: "dark" },

  // Las redes del pie del panel. Vienen de fuera para no tener dos listas que
  // haya que acordarse de cambiar a la vez.
  social: { type: Array, default: () => [] },
});

const emit = defineEmits(["toggle", "toggle-theme", "update:volumen"]);

// ── El player de la cabecera ──
//
// portada 46 · info 264 · volumen 46 · play 46 = 402, y la cuenta a 44. Pulsar la
// portada o la info despliega lo que suena (`PlayerDesplegable`). Abrirlo cierra
// el menú de cuenta y al revés. Cierra al pulsar otra vez, al pulsar fuera y con
// Esc. Solo escritorio: en móvil la celda sigue siendo el play.
const desplegado = ref(false);
const disparador = ref(null);
const posicion = ref({ izquierda: 0, techo: 46 });

const lineaPrograma = computed(() =>
  [props.sonando.programa, props.sonando.franja].filter(Boolean).join(" · "),
);

function colocarDesplegable() {
  const boton = disparador.value;
  if (!boton) return;
  const r = boton.getBoundingClientRect();
  const cabecera = boton.closest(".site-header");
  posicion.value = {
    izquierda: Math.round(r.left),
    techo: cabecera ? Math.round(cabecera.getBoundingClientRect().bottom) : 46,
  };
}

function alternarDesplegable() {
  if (!desplegado.value) colocarDesplegable();
  desplegado.value = !desplegado.value;
}

function fueraDelPlayer(e) {
  if (disparador.value?.contains(e.target)) return;
  if (e.target.closest?.(".desplegable")) return;
  desplegado.value = false;
}

function escapeDelPlayer(e) {
  if (e.key === "Escape") {
    desplegado.value = false;
    disparador.value?.focus();
  }
}

watch(desplegado, (abierto) => {
  if (abierto) {
    cuentaAbierta.value = false;
    idiomaAbierto.value = false;
    document.addEventListener("pointerdown", fueraDelPlayer);
    document.addEventListener("keydown", escapeDelPlayer);
    window.addEventListener("resize", colocarDesplegable);
  } else {
    document.removeEventListener("pointerdown", fueraDelPlayer);
    document.removeEventListener("keydown", escapeDelPlayer);
    window.removeEventListener("resize", colocarDesplegable);
  }
});

// El panel de navegación, solo en móvil. Se cierra solo al cambiar de ruta: sin
// esto, volver atrás con el gesto del navegador dejaba el menú abierto sobre
// una página distinta.
const { t } = useI18n();

const menuAbierto = ref(false);
watch(
  () => props.current,
  () => {
    menuAbierto.value = false;
  },
);

// ── El panel de cuenta ──
//
// Figma `Cabecera · menú de cuenta 1292:5415`. Cuelga del filete, no es modal y
// se cierra pulsando fuera, con Escape o al navegar. El botón que lo abre vive
// en la celda final, después del programa.
const cuentaAbierta = ref(false);
const botonCuenta = ref(null);

// El ancho de la celda final, publicado para el panel. DESIGN.md: «el menú de
// cuenta mide **lo que mida `head-end`** —hoy 296— y se pega al borde derecho».
// La medida no es un número, así que no se escribe como número: se mide.
// El tooltip de las apps: qué dice, bajo qué celda y desde dónde cuelga. Se
// mide al mostrarlo; la cabecera es `sticky`, así que su filete inferior es
// siempre el borde de abajo de la propia cabecera.
const ayuda = ref(null);
function mostrarAyuda({ kicker, nombre, celda }, alinear) {
  const cabecera = celda.closest(".site-header");
  ayuda.value = {
    kicker,
    nombre,
    celda,
    alinear,
    techo: cabecera ? cabecera.getBoundingClientRect().bottom : 46,
  };
}

// Donar va solo con el corazón desde el 21.10.26 (Pablo): lo que es lo dice el
// mismo tooltip que las tiendas.
function ayudaDonar(e) {
  mostrarAyuda({ kicker: t("marco.cabecera.donarKicker"), nombre: t("marco.cabecera.donarNombre"), celda: e.currentTarget }, "centro");
}

// ⛔ Aquí se medía `head-end` para que el menú de cuenta midiera lo mismo. Desde
// el 21.10.26 el menú va a 296 fijo y `head-end` lleva el player de 402.

// 🔴 `pointerdown` y no `click`: con `click`, pulsar el botón cuando el panel
// está abierto lo cerraba aquí y lo volvía a abrir el propio botón en el mismo
// gesto, así que el panel no se cerraba nunca desde su propio botón.
function fuera(e) {
  // `botonCuenta` apunta ahora al componente del botón, no al elemento: el nodo
  // vive en `$el`. Sin esto, pulsar el botón cerraba el panel que acababa de
  // abrir, porque el clic se leía como «fuera».
  if (botonCuenta.value?.$el?.contains(e.target)) return;
  if (e.target.closest?.(".account-menu")) return;
  cuentaAbierta.value = false;
}

function escape(e) {
  if (e.key === "Escape") cuentaAbierta.value = false;
}

watch(cuentaAbierta, (abierta) => {
  if (abierta) desplegado.value = false;
  if (abierta) idiomaAbierto.value = false;
  if (abierta) {
    document.addEventListener("pointerdown", fuera);
    document.addEventListener("keydown", escape);
  } else {
    document.removeEventListener("pointerdown", fuera);
    document.removeEventListener("keydown", escape);
  }
});

// ── El selector de idioma ── (04.11.26, `Pattern/MenuIdioma 2829:1008`)
//
// Un botón con la bandera del idioma actual y la flecha. La lista cuelga del
// filete inferior, pegada al borde derecho, como el menú de cuenta. Se cierra
// al pulsar otra vez, al pulsar fuera, con Esc (el foco vuelve al selector) y al
// elegir. No convive con el menú de cuenta ni con el player desplegado.
const idiomaAbierto = ref(false);
const botonIdioma = ref(null);
const nombreIdioma = computed(() => t("marco.cabecera.idioma", { idioma: NOMBRE_DEL_IDIOMA[IDIOMA] }));

function fueraDelIdioma(e) {
  if (botonIdioma.value?.contains(e.target)) return;
  if (e.target.closest?.(".site-header__menu-idioma")) return;
  idiomaAbierto.value = false;
}

function escapeDelIdioma(e) {
  if (e.key === "Escape") {
    idiomaAbierto.value = false;
    botonIdioma.value?.focus();
  }
}

function elegirIdioma(idioma) {
  idiomaAbierto.value = false;
  cambiaIdioma(idioma);
}

watch(idiomaAbierto, (abierto) => {
  if (abierto) {
    desplegado.value = false;
    cuentaAbierta.value = false;
    document.addEventListener("pointerdown", fueraDelIdioma);
    document.addEventListener("keydown", escapeDelIdioma);
  } else {
    document.removeEventListener("pointerdown", fueraDelIdioma);
    document.removeEventListener("keydown", escapeDelIdioma);
  }
});

// Si la cabecera se va con el panel abierto, los oyentes se quedan sueltos en
// `document` para siempre.
onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", fueraDelIdioma);
  document.removeEventListener("keydown", escapeDelIdioma);
  document.removeEventListener("pointerdown", fuera);
  document.removeEventListener("keydown", escape);
  document.removeEventListener("pointerdown", fueraDelPlayer);
  document.removeEventListener("keydown", escapeDelPlayer);
  window.removeEventListener("resize", colocarDesplegable);
});

watch(
  () => props.current,
  () => {
    cuentaAbierta.value = false;
    idiomaAbierto.value = false;
  },
);

const isActive = (item) =>
  props.current === item.to ||
  (item.to !== "/" && String(props.current).startsWith(String(item.to))) ||
  (item.tambienEn || []).some((prefijo) => String(props.current).startsWith(prefijo));
</script>

<template>
  <header class="site-header">
    <!-- 🔴 **La marca volvió a Astra el 12.09.26.** Estuvo un día siendo
         Dynamo; la decisión de marca se revisó y se deshizo, y el servidor dio
         la orden de vuelta. `DynamoWord` y `DynamoMark` **se quedan en el
         repositorio sin usar**: esta decisión ya ha ido y vuelto una vez, y
         volver a dibujarlos costaría más que tenerlos ahí.

         Se fue con ellos la palabra en móvil, que era de aquella tanda. Aquí
         abajo sigue yendo solo el símbolo, como antes del 11.09. -->
    <router-link to="/" class="site-header__mark" :aria-label="t('marco.cabecera.portada')">
      <AstraWord class="site-header__word" :alto="18" />
      <AstraMark class="site-header__a" :size="32" fondo="ninguno" />
    </router-link>

    <nav class="site-header__nav" :aria-label="t('marco.cabecera.principal')">
      <NavItem
        v-for="item in items"
        :key="item.to"
        :label="item.label"
        :icono="item.icono"
        :to="item.to"
        :active="isActive(item)"
        :destacado="!!item.destacado"
        @mouseenter="item.destacado && ayudaDonar($event)"
        @focus="item.destacado && ayudaDonar($event)"
        @mouseleave="item.destacado && (ayuda = null)"
        @blur="item.destacado && (ayuda = null)"
      />
    </nav>

    <!-- 🔴 **Aquí iba la celda de las tiendas** (TestFlight y Google Play),
         retirada de la cabecera el 04.11.26 a todos los anchos. En el móvil las
         apps bajan al menú (`NavSheet`). `TiendaIcono` se queda en el
         repositorio, como en la Library.

         El aire que sobra va aquí, entre la navegación y el player, y no dentro
         de la navegación, que abraza su contenido. -->
    <div class="site-header__aire" aria-hidden="true" />

    <div class="site-header__end">
      <!-- ── En móvil, el play de siempre ── La celda —su ancho y su rojo
           macizo— la pone la barra. En escritorio va el player de abajo. -->
      <ButtonDs
        class="site-header__play"
        variant="ghost"
        :icono="playing ? 'pause' : 'play'"
        :icono-tam="9"
        :etiqueta="playing ? t('marco.emision.pausar') : t('marco.emision.reproducir')"
        @click="$emit('toggle')"
      />

      <!-- ── El player, en escritorio (21.10.26) ──
           Salen el play pequeño, la onda y el programa: va lo que suena. La
           portada y la info son un solo botón, que despliega el panel. -->
      <!-- La caja del player (`player · radio 4`): sin borde y con radio 4, que
           recorta la portada por la izquierda y el play por la derecha. -->
      <div class="site-header__player">
        <button
          ref="disparador"
          type="button"
          class="site-header__sonando"
          :class="{ 'is-abierto': desplegado }"
          :aria-expanded="desplegado ? 'true' : 'false'"
          aria-controls="player-desplegable"
          :aria-label="t('marco.emision.loQueSuena', { sonando: `${sonando.artista || 'Astra FM'}${sonando.cancion ? ' — ' + sonando.cancion : ''}` })"
          @click="alternarDesplegable"
        >
          <span class="site-header__portada">
            <img v-if="sonando.portada" :src="sonando.portada" alt="" />
          </span>
          <span class="site-header__info">
            <span class="site-header__info-arriba">
              <MiniWave v-if="analisis !== 'no'" class="site-header__onda" :levels="levels" :playing="playing" />
              <SignalDot v-else :playing="playing" />
              <span class="site-header__emision">{{ t("marco.emision.enEmision") }}</span>
              <span v-if="lineaPrograma" class="site-header__info-programa">{{ lineaPrograma }}</span>
            </span>
            <span class="site-header__info-abajo">
              <span class="site-header__info-artista">{{ sonando.artista || "Astra FM" }}</span>
              <span v-if="sonando.cancion" class="site-header__info-cancion">&nbsp;— {{ sonando.cancion }}</span>
            </span>
          </span>
        </button>

        <VolumenEmision
          class="site-header__volumen"
          :model-value="volumen"
          :alto="46"
          abre="abajo"
          @update:model-value="emit('update:volumen', $event)"
        />

        <ButtonDs
          class="site-header__play-grande"
          variant="ghost"
          :icono="playing ? 'pause' : 'play'"
          :icono-tam="14"
          :etiqueta="playing ? t('marco.emision.pausar') : t('marco.emision.reproducir')"
          :lado="46"
          @click="$emit('toggle')"
        />
      </div>

      <!-- ── Idioma y cuenta, juntos ── (04.11.26) A 8 del player, con un solo
           filete a la izquierda y nada entre las dos piezas. -->
      <div class="site-header__idioma-cuenta">
        <button
          ref="botonIdioma"
          type="button"
          class="site-header__idioma"
          :class="{ 'is-abierto': idiomaAbierto }"
          :aria-label="nombreIdioma"
          :title="nombreIdioma"
          aria-haspopup="menu"
          :aria-expanded="idiomaAbierto ? 'true' : 'false'"
          aria-controls="menu-idioma"
          @click="idiomaAbierto = !idiomaAbierto"
        >
          <BanderaIdioma :idioma="IDIOMA" />
          <AstraIcon class="site-header__flecha" name="chevron-down" :size="10" />
        </button>

        <!-- 🔴 Aquí había el botón de contraste. El tema se mudó dentro del panel
             de cuenta —dos celdas con luna y sol—, que es donde lo pone el nodo
             `1292:5415`, y su sitio en la cabecera lo ocupa ahora esta puerta. En
             móvil el panel no existe y el tema sigue en `NavSheet`. -->
        <ButtonDs
          ref="botonCuenta"
          class="site-header__account"
          variant="ghost"
          icono="person-outline"
          :icono-tam="14"
          :etiqueta="t('marco.cabecera.tuCuenta')"
          :aria-expanded="cuentaAbierta ? 'true' : 'false'"
          @click="cuentaAbierta = !cuentaAbierta"
        />
      </div>
    </div>

    <!-- ── La emisión, en móvil ──
         Dos líneas, del nodo `header` 812:3801: **el programa y su género**. En
         escritorio esta celda no se pinta: lo que suena lo dice el player.

         🔴 Antes decía el programa **con su franja horaria** —que no cabe en la
         celda y se cortaba a media palabra— y debajo «Activa · Estable · hora»,
         que es el readout de consola retirado de la cabecera de escritorio el
         07.09 por decir poco. Pablo lo vio en un iPhone el 11.09.26: el género
         dice algo del programa, la hora del reloj no. -->
    <div v-if="programmeName" class="site-header__air">
      <span class="site-header__air-name">{{ programmeName }}</span>
      <span v-if="programmeGenre" class="site-header__air-state">
        {{ programmeGenre }}
      </span>
    </div>

    <!-- Solo en móvil: abre el panel. En escritorio la navegación está a la
         vista y un botón que la esconde sería trabajo de más. -->
    <button
      class="site-header__burger"
      type="button"
      :aria-label="t('marco.cabecera.abrirMenu')"
      :aria-expanded="menuAbierto ? 'true' : 'false'"
      @click="menuAbierto = true"
    >
      <AstraIcon name="menu" :size="20" />
    </button>
  </header>

  <!-- El tooltip de las apps, fuera del `<header>` por lo mismo que el menú de
       cuenta: dentro lo recortaría el `overflow: hidden`. -->
  <TooltipCabecera
    v-if="ayuda"
    :kicker="ayuda.kicker"
    :nombre="ayuda.nombre"
    :celda="ayuda.celda"
    :techo="ayuda.techo"
    :alinear="ayuda.alinear"
  />

  <!-- Fuera del `<header>` a propósito: la cabecera lleva `overflow: hidden`
       para que las celdas que se recortan no asomen, y dentro el panel quedaría
       cortado por el filete. -->
  <!-- 🔴 **296 pegado a la derecha** desde el 21.10.26: ya no mide lo que
       `head-end`, que ahora lleva el player de 402. -->
  <AccountMenu
    :open="cuentaAbierta"
    :ancho="296"
    :theme="theme"
    @close="cuentaAbierta = false"
    @toggle-theme="$emit('toggle-theme')"
  />

  <!-- La lista de idiomas. Fuera del `<header>` por lo mismo que el menú de
       cuenta, y colgando de su filete inferior, pegada a la derecha. -->
  <div v-if="idiomaAbierto" class="site-header__menu-idioma">
    <MenuIdioma @elegir="elegirIdioma" />
  </div>

  <!-- Lo que suena, desplegado. Fuera del `<header>` por lo mismo que el menú de
       cuenta: dentro lo recortaría el `overflow: hidden`. -->
  <PlayerDesplegable
    :open="desplegado"
    :izquierda="posicion.izquierda"
    :techo="posicion.techo"
    :sonando="sonando"
    :playing="playing"
    :levels="levels"
    :analisis="analisis"
  />

  <NavSheet
    :open="menuAbierto"
    :items="items"
    :current="current"
    :theme="theme"
    :social="social"
    @close="menuAbierto = false"
    @toggle-theme="$emit('toggle-theme')"
  />
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

// Flex, no grid. Con celdas que aparecen y desaparecen según el ancho, las
// columnas fijas dejan un hueco muerto a la derecha. Ya pasó una vez.
.site-header {
  position: sticky;
  top: 0;
  z-index: 80;
  display: flex;
  align-items: stretch;
  box-sizing: border-box;
  width: 100%;
  // 62 = 8 de aire + celdas de 46 + 8 de aire. El nodo trae 70 con 12, y Pablo
  // lo bajó a 8 el 04.11.26 al verlo montado. El filete inferior va dentro de
  // los 62: abajo quedan 7 + 1. En móvil vuelve a 46 sin aire (bloque de 880).
  height: var(--altura-cabecera);
  padding: 8px 0 7px;
  background: var(--color-bg-canvas);
  // 🔴 `subtle`, no `strong`. Del 31.08.26: la cabecera no es un módulo del
  // contenido, es el **marco** de la página, y una línea de `line-strong` la
  // convertía en un bloque más que competía con las divisiones de la página. Sus
  // divisiones internas —marca, cada `NavItem`, el estado, la celda final— van en
  // el mismo token, así que la barra entera se dibuja con una sola línea.
  border-bottom: 1px solid var(--color-border-default);
  overflow: hidden;
}

// 🔴 **La celda ya no lleva fondo de color, y aquí va la palabra, no la A.**
// El 07.09 esta celda se pintó de `brand/mark-red` con la A dentro; el 08.09 el
// logotipo pasó a ser la palabra ASTRA entera y ese campo se retiró, porque con
// 128 de ancho era un bloque de color en la esquina que más se mira. Ninguna
// cabecera lleva ya fondo detrás de la marca.
//
// 128 se queda como estaba, y con la palabra de Dynamo el reparto es otro:
// **17 de alto dan 106,82 de ancho y 10,59 de aire por lado**.
//
// ⚠️ Diseño proponía subir la celda a 142 para conservar los ~14 de aire, con la
// cuenta de que `nav` tenía 48 de holgura. **Medido en la cabecera real a 1440
// son 10**, no 48 —su cifra salía del nodo—, así que con 142 la navegación se
// queda en −4 y **se corta «Sobre Astra FM»**. Comprobado forzando la celda
// antes de decidir. Por eso la celda no se toca y la palabra va a 17: un punto
// por encima de su suelo de uso, que es 16.
.site-header__mark {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 128px;
  // Sin filete a la derecha desde el 04.11.26.
  color: var(--color-text-primary);

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }
}

// `width: 0` además de `flex: 1 1 0`: los hijos van en `nowrap` y sin esto
// Chrome dimensiona el contenedor por su min-content. Es la causa de casi todos
// los desbordamientos horizontales del proyecto.
// 🔴 **`nav` tiene suelo, y su suelo es su contenido.** Decisión de diseño del
// 12.09.26 tras tres días con la barra rota: los siete destinos no se recortan,
// no se parten y no se acortan. Es la única celda de la barra que lleva
// información que el visitante no puede sacar de ningún otro sitio.
//
// El suelo se pide con `flex-shrink`, no con `min-width`, y **el reparto es
// desigual a propósito**: `head-end` encoge mil veces más rápido, así que se
// come todo el déficit y `nav` no pierde un píxel mientras al otro le quede
// margen. Antes `nav` era `1 1 0` y encogía hasta cero: entre 1441 y 1452
// «Sobre Astra FM» se salía 12,25 px y se lo comía el borde.
//
// ⚠️ **`shrink: 1` y no `0`.** Con cero, por debajo de 895 —donde `head-end` ya
// está en su mínimo y sigue faltando sitio— el desbordamiento se iba por la
// derecha y **la puerta de la cuenta se salía de la cabecera**, que es peor que
// recortar un destino. Con el reparto desigual, `nav` solo cede cuando ya no
// queda nadie más, y entonces se comporta como antes.
//
// ⚠️ **El suelo es «lo que midan los destinos», no un número.** Si cambia un
// rótulo, cambia el suelo. No hay ninguna cifra escrita en este archivo a
// propósito.
.site-header__nav {
  display: flex;
  // 🔴 **Abraza su contenido** desde el 04.11.26: ya no crece. El aire que
  // sobra se lo queda `aire`, entre la navegación y el player.
  flex: 0 1 auto;
  flex-shrink: 1;
  min-width: 0;
  align-items: stretch;
  overflow: hidden;
  // 🔴 `padding: 0` no es decorativo. astra-styles.scss —la capa legacy que
  // C1 congeló— tiene un `nav { padding: 1.125rem }` global. Sin este reset,
  // el relleno se come 18px arriba y abajo de una cabecera de 46, las celdas
  // se quedan en 9px de alto y los separadores salen como tics cortos con la
  // regla activa flotando a media altura. Se ve, pero no se entiende mirando
  // el componente: la causa está en otro archivo.
  //
  // Muere cuando C4 vacíe esa capa. Hasta entonces, todo componente nuevo que
  // use una etiqueta con estilo legacy —nav, button, a— tiene que blindarse.
  padding: 0;
}

// En escritorio manda la palabra; la A vive escondida hasta el móvil, donde la
// palabra no cabe. Se resuelve con `display` y no con un `v-if` por ancho: el
// SVG que no se ve no se descarga aparte —van los dos en el bundle— y así no
// hay que escuchar el `resize` para nada.
.site-header__a {
  display: none;
}

// ── El aire ── (`aire 2827:6745`) Lo que sobra a la derecha de la navegación.
// Crece y no pide nada: cuando falta sitio es el primero en desaparecer.
.site-header__aire {
  flex: 1 1 0;
  min-width: 0;
}

// ── Player y tema ──
// 🔴 **`head-end` deja de abrazar y pasa a ser la celda que cede.** Toma el aire
// que sobre por encima del suelo de `nav` y, cuando no sobra, se estrecha. Lo
// que se estrecha dentro es el nombre del programa; las horas van `flex: none`.
//
// Es la celda correcta para ceder porque **lo que la hacía crecer es justamente
// lo elástico**: el nombre de lo que esté sonando. Con «Haciendo Cosas Raras ·
// 08–12» pedía 344,25 y con la referencia de diseño 321, y esos 23 px de
// diferencia eran los que rompían la barra según qué sonara.
.site-header__end {
  display: flex;
  flex: none;
  align-items: stretch;
  // Sin filete a la izquierda desde el 04.11.26: el player va en su caja.
  white-space: nowrap;
}

// El play pequeño, solo en móvil (ver el bloque de abajo).
.site-header__play {
  display: none;
  flex: none;
  width: 26px;
  height: 26px;
  align-self: center;
  border: 0;
  background: var(--color-action-primary-default);
  color: var(--color-text-on-action);
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--color-action-primary-hover);
    color: var(--color-text-on-action);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
}

// ── El player ── portada 46 · info 264 · volumen 46 · play 46 = 402.
.site-header__player {
  display: flex;
  align-items: stretch;
  // 🔴 La caja `player · radio 4` (04.11.26): sin borde y con radio 4, la
  // cuarta excepción de § Shapes. El `overflow` recorta la portada y el play.
  border-radius: 4px;
  overflow: hidden;
}

// La portada y la info son un botón. Abierto, los dos en `bg/elevated`, para que
// se lea de dónde sale el panel.
.site-header__sonando {
  display: flex;
  align-items: stretch;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &.is-abierto {
    background: var(--color-bg-elevated);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }
}

.site-header__portada {
  display: block;
  flex: none;
  width: 46px;
  height: 46px;
  overflow: hidden;
  background: var(--color-art-mid);

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

// La onda del player, más alta que la de serie (Pablo, 21.10.26: a 11 «casi
// ni se levantan»): 14, y las barras de 2 a 3 de ancho, para que el movimiento
// se vea en la cabecera.
.site-header__onda {
  height: 14px;
  vertical-align: -3px;

  :deep(.mini-wave__bar) {
    width: 3px;
  }
}

// 264, 16 a los lados y 4 entre líneas. 🔴 Sin la línea de progreso al pie
// (Pablo, 21.10.26): el progreso va solo en el desplegable.
.site-header__info {
  position: relative;
  display: flex;
  flex: 0 1 264px;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  box-sizing: border-box;
  width: 264px;
  min-width: 150px;
  padding: 0 16px;
  overflow: hidden;
  // La información y el volumen, sobre `color/bg/player` (04.11.26).
  background: var(--color-bg-player);
}

.site-header__info-arriba {
  @include t.mono-micro;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  text-transform: uppercase;
  white-space: nowrap;
}

.site-header__emision {
  flex: none;
  color: var(--color-text-accent);
}

.site-header__info-arriba :deep(.mini-wave),
.site-header__info-arriba :deep(.signal-dot) {
  flex: none;
  color: var(--color-action-primary-default);
}

.site-header__info-programa {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--color-text-tertiary);
}

// ARTISTA en mayúsculas y « — Canción» en caja normal, cortado.
.site-header__info-abajo {
  @include t.mono-label;
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.site-header__info-artista {
  color: var(--color-text-primary);
  text-transform: uppercase;
}

.site-header__info-cancion {
  color: var(--color-text-secondary);
  text-transform: none;
}

// El volumen, 46 × 46, y el play, 46 × 46 en el rojo de acción con el icono a 14.
.site-header__volumen {
  background: var(--color-bg-player);
}

.site-header__volumen :deep(.volumen__icono) {
  width: 46px;
}

.site-header__play-grande {
  flex: none;
  height: 46px;
  border: 0;
  background: var(--color-action-primary-default);
  color: var(--color-icon-on-action);

  &:hover:not(:disabled) {
    background: var(--color-action-primary-hover);
    color: var(--color-text-on-action);
  }
}

// ── Idioma y cuenta ── (`idioma + cuenta 2827:6747`) A 8 del player, un solo
// filete `border/default` a la izquierda y nada entre las dos piezas.
.site-header__idioma-cuenta {
  display: flex;
  flex: none;
  align-items: stretch;
  margin-left: 8px;
  border-left: 1px solid var(--color-border-default);
}

// 53 × 46: 14 · bandera 18 · 5 · flecha 10 · 6. Hover y abierto, la flecha en
// acento; abierto, además, apunta arriba, sin giro animado (§ Motion).
.site-header__idioma {
  display: flex;
  flex: none;
  align-items: center;
  gap: 5px;
  margin: 0;
  padding: 0 6px 0 14px;
  border: 0;
  border-radius: 0;
  background: none;
  color: var(--color-icon-secondary);
  cursor: pointer;

  &:hover,
  &.is-abierto {
    color: var(--color-icon-accent);
  }

  &.is-abierto .site-header__flecha {
    transform: rotate(180deg);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
}

.site-header__flecha {
  flex: none;
}

// La lista, colgando del filete inferior de la cabecera y pegada a la derecha.
.site-header__menu-idioma {
  position: fixed;
  top: var(--altura-cabecera);
  right: 0;
  z-index: 90;
}

// 34 × 46: 6 · icono 14 · 14.
.site-header__account {
  flex: none;
  width: auto;
  height: auto;
  align-self: stretch;
  padding: 0 14px 0 6px;
  border: 0;
  color: var(--color-icon-secondary);

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
}

// ── Estrechado ──
// 🔴 **Aquí caía el readout**: se recortaba a la versión corta bajo 1340 y
// desaparecía bajo 1040. Los dos bloques se borran con él, no se adaptan
// (entrega del 07.09.26). Lo que queda cae por orden: el programa a 1200. La
// marca, la navegación y el player no caen nunca.

// 🔴 **Los badges caen antes que nada, y por eso van en su propio corte.**
// Ocupan 256 con su celda, y la navegación es la única que puede encogerse:
// sin esta regla, al estrechar la barra se comería destinos —que es el fallo
// que ya apareció el 08.09 con la palabra del logotipo—.
//
// **El corte es 1440, que es el ancho al que está dibujada la cabecera.** No es
// una cifra redonda elegida a ojo: sumando lo que fija la entrega —marca 128 +
// tiendas 256 + player 296, y 712 de destinos— salían 1392, y con ese número
// **medí un destino cortado a 1401**. La navegación real necesita 712 y a 1401
// se queda en 689. El primer ancho donde entra todo es justo 1440.
// 🔴 Desde el 04.11.26 la celda de tiendas no está a ningún ancho, y con ella
// se fue este corte.

// La celda de emisión. Solo móvil: en escritorio no se pinta.
// El nombre y el género se anclan al borde derecho y crecen hacia la izquierda:
// el hamburguesa queda justo al lado y la lectura empieza donde acaba el texto.
// `align-items: flex-end` encoge cada línea a su contenido; cuando una excede el
// hueco se estira al ancho de la celda y corta con puntos suspensivos.
.site-header__air {
  display: none;
  flex: 1 1 0;
  min-width: 0;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
  padding: 0 12px;
  border-left: 1px solid var(--color-border-default);
  overflow: hidden;
}

.site-header__air-name {
  @include t.mono-micro;
  max-width: 100%;
  color: var(--color-text-primary);
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.site-header__air-state {
  @include t.mono-nano;
  max-width: 100%;
  color: var(--color-text-tertiary);
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// El botón del menú. Celda de 46 como las demás, del nodo móvil.
.site-header__burger {
  display: none;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 46px;
  padding: 0;
  border: 0;
  border-left: 1px solid var(--color-border-default);
  border-radius: 0;
  background: none;
  color: var(--color-icon-primary);
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: -2px;
  }
}

// 🔴 Por debajo del corte, la navegación deja de estar en la barra.
//
// Antes seguía aquí y desbordaba con scroll horizontal. Con seis secciones en
// una pantalla de 390 eso era una lista que había que arrastrar para descubrir
// que existía: medido, cabían dos y media. Los otros tres destinos y medio no
// los veía nadie.
//
// Ahora manda el panel (`NavSheet`), que es lo que dice el nodo móvil.
// ── La palabra solo mientras quepa la navegación ──
//
// 🔴 **La marca vuelve a la A antes de llegar al móvil, y no es estética: es
// que si no, se come un destino.** La celda pasó de 54 a 128 el 08.09, y esos
// 74 px salieron de la navegación, que lleva `overflow: hidden` y recorta sin
// avisar. Medido: los siete destinos suman **725**, y la navegación baja de ahí
// por debajo de **977** de ancho —128 de marca + 725 + 124 del bloque final—.
// Entre 977 y 880 se perdía «Sobre Astra FM» por el borde derecho.
//
// El corte va en 1000, redondeando por arriba con margen. Por debajo entra la A
// en su celda de 46 y la navegación recupera 82 px, de sobra para los 725.
//
// ⚠️ **Este punto lo he calculado midiendo, no sale de ningún nodo**: la
// cabecera de escritorio está dibujada a 1280 y la de móvil a 390, y entre las
// dos no hay nada. Avisado a diseño el 08.09.26.
@media (max-width: 1000px) {
  .site-header__mark {
    width: 46px;
  }

  .site-header__word {
    display: none;
  }

  .site-header__a {
    display: block;
  }
}

@media (max-width: 880px) {
  // 🔴 **El móvil no cambia** (04.11.26): 46 sin aire. `--altura-cabecera` ya
  // vale 46 aquí (tokens.scss); el relleno se quita a mano.
  .site-header {
    padding: 0;
  }

  // Y conserva sus filetes: el de la marca y el de la celda del play, que en
  // escritorio se fueron con la caja del player.
  .site-header__mark {
    border-right: 1px solid var(--color-border-default);
  }

  .site-header__end {
    border-left: 1px solid var(--color-border-default);
  }

  .site-header__nav,
  .site-header__aire {
    display: none;
  }

  .site-header__burger {
    display: flex;
  }

  // La marca ya cambió al símbolo en el bloque de 1000; aquí solo queda decir
  // que en móvil su celda mide lo mismo que las demás de icono —play, menú y
  // ella—. La palabra no entra: a su suelo de 16 mediría 100 de ancho y la celda
  // tiene 46, así que meterla obligaría a rehacer esta cabecera, que es
  // arquitectura y no marca.
  //
  // El símbolo ocupa lo mismo que ocupaba la A —21,4 de alto por 25,2 de ancho,
  // el 78,7 % de su caja de 32—, así que la celda y los márgenes no se mueven.
  //
  // ⚠️ La A era un contorno y el símbolo es una masa sólida: a igualdad de caja
  // **pesa más**. Si en un móvil real grita, no se corrige aquí a ojo, se le
  // dice a diseño y lo ajusta en el archivo.

  // El reparto en móvil: marca · emisión · play · menú.
  //
  // 🔴 El play va **al final, junto al menú**, no pegado a la marca: los dos
  // controles que se tocan quedan en el mismo lado, al alcance del pulgar, y la
  // emisión ocupa el centro flexible. El orden del markup no cambia —el play
  // sigue dentro de `head-end`, que en escritorio va a la derecha de la
  // navegación—, lo reordena `order` solo en este tramo.
  .site-header__mark {
    order: 1;
  }

  .site-header__air {
    order: 2;
  }

  .site-header__end {
    order: 3;
  }

  .site-header__burger {
    order: 4;
  }

  // El player se queda con su celda de 46 y suelta lo que llevaba al lado. El
  // punto y el «Live» se van porque la celda de emisión ya dice si está en
  // directo, con más contexto y sin repetir. Y el interruptor de tema baja al
  // panel, donde el nodo lo pone como una opción más del pie.
  .site-header__end {
    flex: none;
    gap: 0;
    width: 46px;
    padding: 0;
    justify-content: center;
    align-items: center;
  }

  // 🔴 **El móvil no cambia** (21.10.26): el player de escritorio no va, y
  // vuelve el play pequeño de siempre.
  .site-header__player,
  .site-header__idioma-cuenta,
  .site-header__menu-idioma {
    display: none;
  }

  .site-header__play {
    display: flex;
  }

  .site-header__air {
    display: flex;
  }
}
</style>
